/**
 * CR431: Batch URL resolver hook.
 * Resolves multiple storage paths to URLs in a single request via storage-sign-urls.
 * Uses React Query for caching (keyed by sorted path set).
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

/** Public buckets where we can derive URLs without signing */
const PUBLIC_BUCKET_PREFIXES = ["flyer-snippets/"];

function isPublicPath(path: string): boolean {
  return PUBLIC_BUCKET_PREFIXES.some((p) => path.startsWith(p));
}

function resolvePublicUrl(path: string): string {
  // flyer-snippets/foo → public URL
  return `${SUPABASE_URL}/storage/v1/object/public/${path}`;
}

async function batchSignPaths(paths: string[]): Promise<Record<string, string | null>> {
  if (paths.length === 0) return {};

  // Separate public from private
  const result: Record<string, string | null> = {};
  const privatePaths: string[] = [];

  for (const p of paths) {
    if (isPublicPath(p)) {
      result[p] = resolvePublicUrl(p);
    } else {
      privatePaths.push(p);
    }
  }

  if (privatePaths.length === 0) return result;

  // Call the batch signing edge function
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) {
    // No auth — return nulls for private paths
    for (const p of privatePaths) result[p] = null;
    return result;
  }

  const resp = await fetch(`${SUPABASE_URL}/functions/v1/storage-sign-urls`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify({ paths: privatePaths, expiresInSeconds: 3600 }),
  });

  if (!resp.ok) {
    console.warn("[useSignedStorageUrls] batch sign failed:", resp.status);
    for (const p of privatePaths) result[p] = null;
    return result;
  }

  const json = await resp.json();
  const urlsByPath = json.urlsByPath as Record<string, string | null>;
  Object.assign(result, urlsByPath);
  return result;
}

/**
 * Batch-resolve an array of storage paths to URLs.
 * Returns a stable map of path→url (null if missing/error).
 * Cached for 50 minutes (signed URLs valid for 60min).
 */
export function useSignedStorageUrls(paths: (string | null | undefined)[]) {
  const validPaths = paths.filter((p): p is string => !!p);
  const sortedKey = [...validPaths].sort().join("|");

  const { data: urlMap = {}, isLoading } = useQuery({
    queryKey: ["storage-sign-urls", sortedKey],
    queryFn: () => batchSignPaths(validPaths),
    enabled: validPaths.length > 0,
    staleTime: 50 * 60 * 1000, // 50 min
    gcTime: 60 * 60 * 1000,    // 60 min
  });

  return {
    urlMap: urlMap as Record<string, string | null>,
    loading: isLoading && validPaths.length > 0,
    resolveUrl: (path: string | null | undefined): string | null => {
      if (!path) return null;
      return urlMap[path] ?? null;
    },
  };
}
